import React, { HTMLAttributes, useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";
import {
  getMonthlyOrdersByPhone,
  updateMonthlyOrder,
} from "../../../apis/order";
import type { IGetMonthlyOrderResponse } from "../../../apis/order/type";
import {
  adyenGetPaymentMethods,
  createCustomerPortal,
  precancelSubscription,
} from "../../../apis/subscription";
import type { IStoredPaymentMethod } from "../../../apis/subscription/type";
import { ReactComponent as HgvIcon } from "../../../assets/icons/HgvIcon.svg";
import { ReactComponent as LgvIcon } from "../../../assets/icons/LgvIcon.svg";
import { ReactComponent as BikeIcon } from "../../../assets/icons/bikeIcon.svg";
import { ReactComponent as CarIcon } from "../../../assets/icons/carIcon.svg";
import { ReactComponent as InfoIcon } from "../../../assets/icons/infoIcon.svg";
import RefreshIcon from "../../../assets/icons/refetch.png";
import { getExtractParts, toHK } from "../../../helper/format";
import { customShowToast } from "../../../helper/toast";
import { ROUTE_PATHS } from "../../../routes/paths/route-paths";
import useGeneralStore from "../../../store/general";
import useSubscriptionRecordsStore from "../../../store/subscriptionRecord";
import BaseButton from "../../atom/baseButton";
import BaseChip from "../../atom/baseChip";
import BaseDarkButton from "../../atom/baseDarkButton";
import BaseModal from "../../atom/baseModal";
import BaseOctopusNumberTextInput from "../../atom/baseOctopusTextInput";
import BaseTitle from "../../atom/baseTitle";
import ModifyExtraCarplateBlock from "../modifyExtraCarplateBlock";
import RelinkSubscriptionCreditCardModal from "../relinkSubscriptionCreditCardModal";

export const ITEM_TYPE_MAPPING: Record<
  number,
  { title: string; icon: React.ReactNode }
> = {
  1: {
    title: "私家車／客貨車",
    icon: <CarIcon />,
  },
  2: {
    title: "電單車",
    icon: <BikeIcon />,
  },
  3: {
    title: "LGV輕型貨車",
    icon: <LgvIcon />,
  },
  4: {
    title: "HGV重型貨車",
    icon: <HgvIcon />,
  },
  5: {
    title: "戶外貨車",
    icon: <HgvIcon />,
  },
};

export const ORDER_STATE_MAPPING: Record<
  number,
  { label: string; color: string }
> = {
  1: { label: "待啟用", color: "text-mainYellow" },
  2: { label: "訂閱中", color: "text-green-500" },
  7: { label: "待取消", color: "text-orange-500" },
  4: { label: "已取消", color: "text-alertRed" },
  8: { label: "已失效", color: "text-alertRed" },
};

interface ISubscriptionManageBlock {}

type TSubscriptionManageBlockProps = ISubscriptionManageBlock &
  HTMLAttributes<HTMLDivElement>;

const SubscriptionManageBlock: React.FC<TSubscriptionManageBlockProps> = (
  props
) => {
  const { ...otherProps } = props;
  const setGlobalLoadingState = useGeneralStore((state) => state.setIsLoading);

  // const [subscriptionData, setSubscriptionData] = useState<
  //   IGetMonthlyOrderResponse[]
  // >([]);

  // show alert for go to stripe management page
  const [showAlertModal, setShowAlertModal] = useState(false);

  // show modify adyen credit card modal
  const [showCardModal, setShowCardModal] = useState<{
    shopperRef: string | undefined;
    storedPaymentMethods: IStoredPaymentMethod | undefined;
  }>({
    shopperRef: undefined,
    storedPaymentMethods: undefined,
  });

  // show cancel subscription modal for adyen subscription
  const [showCancelModal, setShowCancelModal] = useState("");

  // octopus number modify modal
  const [showModifyOctopusNumberModal, setShowModifyOctopusNumberModal] =
    useState({
      monthlyOrderId: "",
      octopusNumber: "",
      octopusLastDigit: "",
    });

  const [showModifyExtraCarplateModal, setShowModifyExtraCarplateModal] =
    useState<IGetMonthlyOrderResponse | null>();
  const [managementUrl, setManagementUrl] = useState("");

  const subscriptionRecord = useSubscriptionRecordsStore(
    (state) => state.subscriptionRecordList
  );
  const subscriptionRecordSetter = useSubscriptionRecordsStore(
    (state) => state.setSubscriptionRecordList
  );

  const onCloseAlertModal = () => {
    setShowAlertModal(false);
  };

  const onCloseModifyOctopusNumberModal = () => {
    setShowModifyOctopusNumberModal({
      monthlyOrderId: "",
      octopusNumber: "",
      octopusLastDigit: "",
    });
  };

  const onCloseCancelModal = () => {
    setShowCancelModal("");
  };

  const onCloseModifyExtraCarplateModal = () => {
    setShowModifyExtraCarplateModal(null);
  };

  const onCloseCardModal = () => {
    setShowCardModal({
      shopperRef: undefined,
      storedPaymentMethods: undefined,
    });
  };

  const updateOctopusNumber = async () => {
    try {
      const finalOctopusNumber = showModifyOctopusNumberModal.octopusNumber
        ? `${showModifyOctopusNumberModal.octopusNumber}(${showModifyOctopusNumberModal.octopusLastDigit})`
        : undefined;

      if (finalOctopusNumber) {
        const res = await updateMonthlyOrder(
          showModifyOctopusNumberModal.monthlyOrderId,
          finalOctopusNumber
        );
        if (res) {
          onCloseModifyOctopusNumberModal();
          fetchSubscriptionHistory();
          customShowToast("八達通編號已更新", "success", () => {});
        }
      }
    } catch (error) {
      console.error("Error updating octopus number:", error);
    }
  };

  const precancelSubscriptionHandler = async (monthly_order_id: string) => {
    try {
      setGlobalLoadingState(true);
      const precancelResult = await precancelSubscription(monthly_order_id);
      if (precancelResult) {
        fetchSubscriptionHistory();
        setGlobalLoadingState(false);
      }
    } catch (error) {
      setGlobalLoadingState(false);
    }
  };

  const fetchStoredPaymentMethod = async (
    shopperRef: string,
    storedPaymentMethodId: string
  ) => {
    try {
      if (shopperRef && storedPaymentMethodId) {
        setGlobalLoadingState(true);

        let paymentMethodObject = await adyenGetPaymentMethods(
          shopperRef,
          storedPaymentMethodId
        );

        if (paymentMethodObject) {
          setShowCardModal({
            shopperRef,
            storedPaymentMethods: paymentMethodObject,
          });

          setGlobalLoadingState(false);
        }
      }
    } catch (error) {
      setShowCardModal({
        shopperRef: undefined,
        storedPaymentMethods: undefined,
      });
      setGlobalLoadingState(false);
    }
  };

  const fetchCreateStripeCustomerPortal = async (customerId: string) => {
    try {
      const portalResponse = await createCustomerPortal(customerId);
      if (portalResponse.url) {
        setManagementUrl(portalResponse.url);
        setShowAlertModal(true);
      }
    } catch (error) {
      console.log("error?");
    }
  };

  // fetch subscription history
  const fetchSubscriptionHistory = async () => {
    try {
      const loginState = JSON.parse(
        localStorage.getItem("wbloginState") || "{}"
      );

      setGlobalLoadingState(true);
      const inquireResult = await getMonthlyOrdersByPhone(loginState.phone); // TODO
      if (inquireResult) {
        subscriptionRecordSetter(inquireResult);
        setGlobalLoadingState(false);
        setShowModifyExtraCarplateModal(null);
      }
    } catch (error) {
      setGlobalLoadingState(false);
    } finally {
      setGlobalLoadingState(false);
    }
  };

  // fetch subscription history on mount and every 2 mins
  useEffect(() => {
    fetchSubscriptionHistory();

    const interval = setInterval(() => {
      fetchSubscriptionHistory();
    }, 120000); // 2 minutes

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="flex flex-col gap-4 px-4" {...otherProps}>
      <div className="flex flex-row justify-between items-center">
        <BaseTitle text="訂閱記錄" />
        <button
          className="flex flex-row items-center"
          onClick={() => {
            fetchSubscriptionHistory();
          }}
        >
          <div className="text-white text-xs">刷新</div>
          <img src={RefreshIcon} width={14} height={14} />
        </button>
      </div>
      {subscriptionRecord.length === 0 && (
        <div className="flex flex-col rounded-2xl overflow-hidden">
          <div className="bg-mainDark p-4 text-white">暫無訂閱記錄</div>
        </div>
      )}

      <div className="">
        {subscriptionRecord?.length > 0 &&
          subscriptionRecord.map((item, index) => {
            // console.log(item.id);
            const isFreetrail = item.monthly_order_state_id === 1;
            const isActive = item.monthly_order_state_id === 2;
            const isPreCancel = item.monthly_order_state_id === 7;
            const isCanceled = item.monthly_order_state_id === 4;

            const itemInfo =
              ITEM_TYPE_MAPPING[item.monthlyOrderItem?.item_type_id];
            const stateObject =
              ORDER_STATE_MAPPING[item.monthly_order_state_id ?? 8];

            if (stateObject) {
              return (
                <div
                  className={twMerge(
                    "flex flex-col rounded-2xl overflow-hidden mb-4",
                    isCanceled ? "opacity-50" : ""
                  )}
                  key={index}
                >
                  {/* header section */}
                  <div className="bg-[#0D1014] p-4">
                    <div className="flex flex-row justify-between items-center text-xs text-white mb-2">
                      <div>
                        落單時間{" "}
                        {toHK(item.createdAt).format("YYYY-MM-DD HH:mm")}
                      </div>
                      <div className={stateObject?.color}>
                        {stateObject.label}
                      </div>
                    </div>

                    <span className="text-xs text-white">
                      訂單編號 {item.ref_no}
                    </span>
                  </div>

                  {/* body section */}
                  <div className="bg-mainDark p-4 text-white">
                    <div className="flex flex-col gap-4">
                      <div className="flex flex-col gap-1">
                        {item.monthlyOrderItem.interval_count && (
                          <BaseChip
                            leftIcon={<InfoIcon />}
                            text={
                              item.monthlyOrderItem.interval_count > 1
                                ? `${item.monthlyOrderItem.interval_count}個月季票計劃`
                                : "月票"
                            }
                          />
                        )}

                        <span className="font-bold text-xl">
                          {item.parking.display_name}
                        </span>

                        <div className="flex flex-row gap-2 items-center">
                          {itemInfo.icon}
                          <div className="text-xs font-bold">
                            {itemInfo.title}
                          </div>
                        </div>

                        <div className="flex flex-1 flex-row gap-1 items-baseline text-xs font-bold">
                          <span>
                            <span>
                              {item.monthlyOrderItem.interval_count > 1
                                ? "每月平均約"
                                : "每個月"}
                            </span>
                          </span>
                          <span className="text-mainYellow text-xl">
                            {(
                              item.currentOrderPrice.main_car_plate_price /
                              item.monthlyOrderItem.interval_count
                            ).toLocaleString("zh-HK", {
                              style: "currency",
                              currency: "HKD",
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0,
                            })}
                          </span>
                          {/* {item.monthlyOrderItem.interval_count > 1 && (
                            <span>*需要一次性付款</span>
                          )} */}
                        </div>
                      </div>

                      <div className="flex flex-row gap-1 text-xs justify-between">
                        <div className="flex flex-col gap-1 text-xs">
                          <div>車牌號碼: {item.u_lpn}</div>
                          <div>
                            {item.octopus_number
                              ? `八達通編號: ${item.octopus_number}`
                              : ""}
                          </div>
                        </div>
                        {/* date section */}
                        <div className="flex flex-col gap-1 text-xs">
                          <div>
                            開始日期:{" "}
                            {toHK(item.start_date).format("YYYY-MM-DD")}
                          </div>
                          {!isActive && !isFreetrail && (
                            <div>
                              完結日期:{" "}
                              {toHK(item.end_date).format("YYYY-MM-DD")}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* button section 1 */}
                      <div className="flex flex-row flex-1 justify-between items-center gap-4">
                        {(isFreetrail || isActive) && (
                          <BaseDarkButton
                            onClick={(e) => {
                              const { numberPart, insideParentheses } =
                                getExtractParts(item.octopus_number || "");

                              setShowModifyOctopusNumberModal({
                                monthlyOrderId: item.id.toString(),
                                octopusNumber: numberPart || "",
                                octopusLastDigit: insideParentheses || "",
                              });
                            }}
                            className="flex-1 "
                          >
                            {item.octopus_number
                              ? "修改八達通編號"
                              : "新增八達通編號"}
                          </BaseDarkButton>
                        )}
                      </div>

                      {/* button section 2 */}
                      <div className="flex flex-row flex-1 justify-between items-center gap-4">
                        {/* non stripe management button */}
                        {item.subscription_id === null && (
                          <BaseDarkButton
                            onClick={(e) => {
                              const token = item.token; // Replace with the actual token from your data
                              const url =
                                ROUTE_PATHS.SUBSCRIPTION_PAYMENT_HISTORY_PAGE.replace(
                                  ":token",
                                  token
                                ); // Replace :token with the actual token

                              window.open(url, "_blank");
                            }}
                            className="flex-1 "
                          >
                            查看賬單
                          </BaseDarkButton>
                        )}

                        {/* stripe management button */}
                        {!isCanceled && item.subscription_id && (
                          <BaseDarkButton
                            onClick={(e) => {
                              e.stopPropagation();
                              fetchCreateStripeCustomerPortal(item.customer_id);
                            }}
                            className="flex-1 "
                          >
                            訂閱管理
                          </BaseDarkButton>
                        )}

                        {!isCanceled && (
                          <BaseButton
                            onClick={() =>
                              setShowModifyExtraCarplateModal(item)
                            }
                            className="flex-1 border-mainYellow"
                          >
                            一位多泊計劃
                          </BaseButton>
                        )}
                      </div>

                      {/* button section 2 */}
                      <div className="flex flex-row flex-1 justify-between items-center gap-4">
                        {isActive && !item.subscription_id && (
                          <BaseButton
                            onClick={
                              (e) => setShowCancelModal(item.id.toString())
                              // precancelSubscriptionHandler(item.id.toString())
                            }
                            className="flex-1 bg-alertRed text-white hover:bg-red-500 border-alertRed"
                          >
                            取消訂閱
                          </BaseButton>
                        )}

                        {!item.subscription_id && (
                          <BaseButton
                            disabled={isCanceled}
                            onClick={() => {
                              if (item.storedPaymentMethodId) {
                                fetchStoredPaymentMethod(
                                  `monthlyOrderId-${item.id}`,
                                  item.storedPaymentMethodId
                                );
                              } else {
                                setShowCardModal({
                                  shopperRef: `monthlyOrderId-${item.id}`,
                                  storedPaymentMethods: undefined,
                                });
                              }
                            }}
                            className="flex-1 bg-emerald-700 text-white hover:bg-emerald-600 border-emerald-700"
                          >
                            {item.storedPaymentMethodId
                              ? "更改信用卡"
                              : "新增信用卡"}
                          </BaseButton>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            } else {
              return null;
            }
          })}
      </div>

      {/* extra car plate popup */}
      {!!showModifyExtraCarplateModal && (
        <BaseModal
          titleStyle="text-white font-bold text-2xl "
          modalBaseStyle="h-full max-w-[400px] bg-selectedDark"
          showCloseButton={true}
          open={!!showModifyExtraCarplateModal}
          onClose={onCloseModifyExtraCarplateModal}
        >
          <ModifyExtraCarplateBlock
            item={showModifyExtraCarplateModal!}
            refetchListCallback={fetchSubscriptionHistory}
          />
        </BaseModal>
      )}

      {/* modify credit card modal */}
      {showCardModal.shopperRef && (
        <RelinkSubscriptionCreditCardModal
          shopperReference={showCardModal.shopperRef!}
          storedPaymentMethods={showCardModal.storedPaymentMethods ?? null}
          open={showCardModal.shopperRef !== undefined}
          onClose={onCloseCardModal}
          refetchMonthlyOrder={fetchSubscriptionHistory}
        />
      )}

      {/* confirm cancel subscription modal */}
      <BaseModal
        title="取消訂閱"
        titleStyle="text-white font-bold text-2xl"
        customWidth="w-96"
        modalBaseStyle="bg-black"
        open={showCancelModal !== ""}
        onClose={onCloseCancelModal}
      >
        <div className="flex flex-col gap-4 text-white">
          <span className="py-4">
            取消訂閱後，在結束日期前您仍保持車位使用權，而下期賬單亦不會自動扣款。
          </span>
          <div className="flex flex-row justify-between items-center gap-4">
            <BaseDarkButton onClick={onCloseCancelModal} className="flex-1 ">
              取消
            </BaseDarkButton>
            <BaseButton
              onClick={() => {
                precancelSubscriptionHandler(showCancelModal);
                onCloseCancelModal();
              }}
              className="flex-1"
            >
              確認
            </BaseButton>
          </div>
        </div>
      </BaseModal>

      {/* go stripe panel modal */}
      <BaseModal
        title="前往管理頁面"
        titleStyle="text-white font-bold text-2xl"
        customWidth="w-96"
        modalBaseStyle="bg-black"
        open={showAlertModal}
        onClose={onCloseAlertModal}
      >
        <div className="flex flex-col gap-4 text-white">
          <span className="py-4">
            您可以透過管理頁面查看收據，取消訂閱或更新信用卡資料。
          </span>
          <div className="flex flex-row justify-between items-center gap-4">
            <BaseDarkButton onClick={onCloseAlertModal} className="flex-1 ">
              取消
            </BaseDarkButton>
            <BaseButton
              onClick={() => {
                window.open(managementUrl, "_blank");
                onCloseAlertModal();
              }}
              className="flex-1"
            >
              確認
            </BaseButton>
          </div>
        </div>
      </BaseModal>

      {/* modify octopus number */}
      <BaseModal
        title="修改八達通編號"
        titleStyle="text-white font-bold text-2xl"
        customWidth="w-96"
        modalBaseStyle="bg-black"
        open={showModifyOctopusNumberModal.monthlyOrderId !== ""}
        onClose={onCloseModifyOctopusNumberModal}
      >
        <div className="flex flex-col gap-4 text-white">
          <BaseOctopusNumberTextInput
            octopusNumber={showModifyOctopusNumberModal.octopusNumber}
            octopusLastDigit={showModifyOctopusNumberModal.octopusLastDigit}
            onchangeBookingFormValues={(key, value) => {
              setShowModifyOctopusNumberModal((prev) => ({
                ...prev,
                [key]: value,
              }));
            }}
          />
          <div className="flex flex-row justify-between items-center gap-4">
            <BaseDarkButton
              onClick={onCloseModifyOctopusNumberModal}
              className="flex-1 "
            >
              取消
            </BaseDarkButton>
            <BaseButton
              disabled={
                showModifyOctopusNumberModal.octopusNumber === "" ||
                showModifyOctopusNumberModal.octopusLastDigit === ""
              }
              onClick={updateOctopusNumber}
              className="flex-1"
            >
              確認
            </BaseButton>
          </div>
        </div>
      </BaseModal>
    </div>
  );
};

export default SubscriptionManageBlock;
